import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2 } from 'lucide-react';
import { meetingsAPI } from '../../lib/api';

const SECTIONS = [
  ['people', 'People'],
  ['coverage', 'Coverage'],
  ['relationship', 'Relationship'],
  ['actions', 'Actions'],
  ['results', 'Results'],
  ['market_feedback', 'Market feedback'],
  ['review_discipline', 'Review discipline'],
  ['visits_by_person', 'Visits by person'],
];

const FETCHERS = {
  people: meetingsAPI.reportPeople,
  coverage: meetingsAPI.reportCoverage,
  relationship: meetingsAPI.reportRelationship,
  actions: meetingsAPI.reportActions,
  results: meetingsAPI.reportResults,
  market_feedback: meetingsAPI.reportMarketFeedback,
  review_discipline: meetingsAPI.reportReviewDiscipline,
  visits_by_person: meetingsAPI.reportVisitsByPerson,
};

// Sections that take a scope (mine/team/all); Results and Review discipline are
// MEETINGS EDIT only (the backend 403s anyone else, so they never take a scope param).
const SCOPED_SECTIONS = new Set(['people', 'coverage', 'relationship', 'actions', 'visits_by_person']);

function monthToRange(month) {
  if (!month) return {};
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  return { date_from: `${month}-01`, date_to: `${month}-${String(last).padStart(2, '0')}` };
}

export default function ReportsTab({ capabilities }) {
  // Part B4h (26 Oct 2026, Lex's review): each report shows only to the people allowed —
  // Results/Review discipline are MEETINGS EDIT only; Actions never for reviewer_ea_profile
  // (who sees only this tab, with People only, since coverage/relationship/visits-by-person
  // already require MEETINGS CREATE which they don't hold).
  const visibleSections = SECTIONS.filter(([key]) => {
    if (key === 'people') return capabilities.can_view_people_report;
    if (key === 'market_feedback') return capabilities.can_view_market_report;
    if (key === 'actions') return capabilities.can_view_actions_report;
    if (key === 'results' || key === 'review_discipline') return capabilities.can_admin;
    return capabilities.can_log; // Coverage/Relationship/Visits by person need MEETINGS CREATE
  });
  const [section, setSection] = useState(visibleSections[0]?.[0] || 'people');
  // reviewer_ea_profile always gets global People data from the backend (scope is ignored),
  // so offering "My own data" to them would be a misleading label.
  const [scope, setScope] = useState(capabilities.is_reviewer_ea ? 'all' : 'mine');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  // Which section the current `data` belongs to - prevents rendering the previous
  // section's payload with the newly-clicked section's renderer (crash on shape mismatch).
  const [dataSection, setDataSection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const scopeOptions = capabilities.is_reviewer_ea && !capabilities.can_admin ? [] : [['mine', 'My own data']];
  if (capabilities.is_manager) scopeOptions.push(['team', 'My team']);
  if (capabilities.can_admin || capabilities.is_reviewer_ea) scopeOptions.push(['all', 'Everyone']);

  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const mySeq = ++requestSeq.current;
    setLoading(true); setData(null); setError(null);
    try {
      const params = {};
      if (SCOPED_SECTIONS.has(section)) params.scope = scope;
      if (section === 'people') Object.assign(params, monthToRange(month));
      const res = await FETCHERS[section](params);
      // A later click may have already fired a newer request — an earlier, slower
      // response landing after it must never stomp the current section's data.
      if (mySeq !== requestSeq.current) return;
      setData(res.data); setDataSection(section);
    } catch (e) {
      if (mySeq !== requestSeq.current) return;
      setData(null); setDataSection(null); setError(e.response?.data?.detail || 'Failed to load this report');
    } finally {
      if (mySeq === requestSeq.current) setLoading(false);
    }
  }, [section, scope, month]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="meetings-reports-tab">
      <div className="flex flex-wrap gap-2">
        {visibleSections.map(([key, label]) => (
          <button key={key} type="button" onClick={() => setSection(key)} data-testid={`report-section-${key}`}
            className={`px-3 py-1.5 text-sm rounded-sm border ${section === key ? 'bg-foreground text-background border-foreground' : 'bg-background hover:bg-muted/60'}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap items-end gap-3">
        {SCOPED_SECTIONS.has(section) && (
          <div className="w-full sm:w-56">
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger data-testid="report-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent>{scopeOptions.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {section === 'people' && (
          <div><Label className="text-xs">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-people-month" /></div>
        )}
      </div>

      {loading && <div className="flex justify-center py-8" data-testid="report-loading"><Loader2 className="animate-spin" /></div>}

      {!loading && error && (
        <Card className="rounded-sm"><CardContent className="p-4 text-sm text-red-700" data-testid="report-error">{String(error)}</CardContent></Card>
      )}

      {!loading && !error && data && dataSection === section && (
        <Card className="rounded-sm"><CardContent className="p-4 overflow-x-auto" data-testid={`report-content-${section}`}>
          <ReportBody section={section} data={data} isEdit={capabilities.can_admin} />
        </CardContent></Card>
      )}
    </div>
  );
}

function Th({ children }) { return <th className="py-1 pr-3 whitespace-nowrap">{children}</th>; }
function Td({ children }) { return <td className="py-1 pr-3 whitespace-nowrap">{children}</td>; }
const usd = (v) => (v == null ? '—' : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`);
const pct = (v) => (v == null ? '—' : `${v}%`);

function ReportBody({ section, data, isEdit }) {
  const rows = data.rows || [];

  if (section === 'people') {
    return (
      <table className="w-full text-sm" data-testid="people-report-body">
        <thead><tr className="text-left border-b">
          <Th>Name</Th><Th>Month</Th><Th>Attended</Th><Th>Logged</Th><Th>In person</Th><Th>Virtual</Th>
          <Th>Vendor</Th><Th>Customer</Th><Th>Planned</Th><Th>Unplanned</Th><Th>Target</Th><Th>% of target</Th>
          <Th>On-time %</Th><Th>Avg delay (hrs)</Th><Th>Late</Th><Th>Missing</Th><Th>Sent back</Th><Th>Open actions</Th><Th>Overdue actions</Th>
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.user_id}-${r.month}`} className="border-b last:border-0" data-testid={`people-row-${r.user_id}-${r.month}`}>
            <Td>{r.name}</Td><Td>{r.month}</Td><Td>{r.attended}</Td><Td>{r.logged}</Td><Td>{r.physical}</Td><Td>{r.virtual}</Td>
            <Td>{r.vendor}</Td><Td>{r.customer}</Td><Td>{r.planned}</Td><Td>{r.unplanned}</Td><Td>{r.target}</Td><Td>{pct(r.pct_of_target)}</Td>
            <Td>{pct(r.on_time_pct)}</Td><Td>{r.avg_delay_hours ?? '—'}</Td><Td>{r.late}</Td><Td>{r.missing}</Td><Td>{r.sent_back}</Td>
            <Td>{r.open_actions}</Td><Td>{r.overdue_actions}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (section === 'coverage') {
    return (
      <table className="w-full text-sm" data-testid="coverage-report-body">
        <thead><tr className="text-left border-b">
          <Th>Company</Th><Th>Type</Th><Th>Owner</Th><Th>Last met</Th><Th>Last rating</Th><Th>Days since</Th>
          <Th>Not met 30d</Th><Th>Not met 60d</Th><Th>Not met 90d</Th>{isEdit && <Th>Business last 12m (USD)</Th>}
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.company_type}-${r.company_ref}`} className="border-b last:border-0" data-testid={`coverage-row-${r.company_ref}`}>
            <Td>{r.company_name}</Td><Td>{r.company_type}</Td><Td>{r.owner_name || '—'}</Td>
            <Td>{r.last_met_at ? r.last_met_at.slice(0, 10) : 'Never'}</Td><Td>{r.last_rating ?? '—'}</Td><Td>{r.days_since_last_met ?? '—'}</Td>
            <Td>{r.not_met_30 ? 'Yes' : 'No'}</Td><Td>{r.not_met_60 ? 'Yes' : 'No'}</Td><Td>{r.not_met_90 ? 'Yes' : 'No'}</Td>
            {isEdit && <Td>{usd(r.business_last_12m_usd)}</Td>}
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (section === 'relationship') {
    return (
      <table className="w-full text-sm" data-testid="relationship-report-body">
        <thead><tr className="text-left border-b"><Th>Company</Th><Th>Latest rating</Th><Th>Latest date</Th><Th>Previous rating</Th><Th>Previous date</Th></tr></thead>
        <tbody>{rows.length === 0 ? (
          <tr><Td>No companies currently need attention.</Td></tr>
        ) : rows.map((r) => (
          <tr key={`${r.company_type}-${r.company_ref}`} className="border-b last:border-0" data-testid={`relationship-row-${r.company_ref}`}>
            <Td>{r.company_name}</Td><Td>{r.latest_rating}</Td><Td>{r.latest_at?.slice(0, 10)}</Td>
            <Td>{r.previous_rating ?? '—'}</Td><Td>{r.previous_at ? r.previous_at.slice(0, 10) : '—'}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (section === 'actions') {
    return (
      <table className="w-full text-sm" data-testid="actions-report-body">
        <thead><tr className="text-left border-b"><Th>Name</Th><Th>Open</Th><Th>Overdue</Th><Th>Oldest overdue</Th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.user_id} className="border-b last:border-0" data-testid={`actions-row-${r.user_id}`}>
            <Td>{r.name}</Td><Td>{r.open}</Td><Td>{r.overdue}</Td><Td>{r.oldest_overdue ?? '—'}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (section === 'results') {
    return (
      <table className="w-full text-sm" data-testid="results-report-body">
        <thead><tr className="text-left border-b"><Th>Company</Th><Th>Date</Th><Th>Logger</Th><Th>Sales opp. (USD)</Th><Th>Purchase opp. (USD)</Th><Th>Business done 60d (USD)</Th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.meeting_id} className="border-b last:border-0" data-testid={`results-row-${r.meeting_id}`}>
            <Td>{r.company_name}</Td><Td>{r.start_at?.slice(0, 10)}</Td><Td>{r.logger_name}</Td>
            <Td>{usd(r.sales_opportunity_usd)}</Td><Td>{usd(r.purchase_opportunity_usd)}</Td><Td>{usd(r.business_done_60d_usd)}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (section === 'market_feedback') {
    const kinds = [['issues', 'Issues'], ['price_feedback', 'Price feedback'], ['bid_feedback', 'Bid feedback'], ['competitors', 'Competitors']];
    const dims = [['by_country', 'By country'], ['by_category', 'By product category'], ['by_month', 'By month']];
    return (
      <div className="space-y-6 text-sm" data-testid="market-feedback-report-body">
        {dims.map(([dimKey, dimLabel]) => (
          <div key={dimKey}>
            <p className="text-xs font-semibold text-muted-foreground mb-2">{dimLabel}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {kinds.map(([kindKey, kindLabel]) => {
                const byDim = (data[kindKey] || {})[dimKey] || {};
                const dimValues = Object.keys(byDim);
                return (
                  <div key={kindKey} data-testid={`market-feedback-${kindKey}-${dimKey}`}>
                    <p className="text-xs text-muted-foreground mb-1">{kindLabel}</p>
                    {dimValues.length === 0 ? <p className="text-xs text-muted-foreground">No data</p> : (
                      <ul className="space-y-1">{dimValues.map((dv) => (
                        <li key={dv}>
                          <span className="font-medium">{dv}:</span>{' '}
                          {Object.entries(byDim[dv]).map(([fv, count]) => `${fv} (${count})`).join(', ')}
                        </li>
                      ))}</ul>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {data.rows && (
          <div>
            <p className="text-xs text-muted-foreground mb-1 mt-2">Detail (with company names)</p>
            <table className="w-full text-sm"><thead><tr className="text-left border-b"><Th>Company</Th><Th>Country</Th><Th>Month</Th></tr></thead>
              <tbody>{data.rows.map((r, i) => <tr key={i} className="border-b last:border-0"><Td>{r.company_name}</Td><Td>{r.country}</Td><Td>{r.month}</Td></tr>)}</tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
  if (section === 'review_discipline') {
    return (
      <table className="w-full text-sm" data-testid="review-discipline-report-body">
        <thead><tr className="text-left border-b"><Th>Reviewer</Th><Th>Waiting</Th><Th>Overdue</Th><Th>Oldest waiting since</Th><Th>Avg turnaround (hrs)</Th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.reviewer_id} className="border-b last:border-0" data-testid={`review-discipline-row-${r.reviewer_id}`}>
            <Td>{r.reviewer_name}</Td><Td>{r.waiting}</Td><Td>{r.overdue}</Td>
            <Td>{r.oldest_waiting_at ? r.oldest_waiting_at.slice(0, 10) : '—'}</Td><Td>{r.average_turnaround_hours ?? '—'}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  if (section === 'visits_by_person') {
    return (
      <table className="w-full text-sm" data-testid="visits-by-person-report-body">
        <thead><tr className="text-left border-b">
          <Th>Name</Th><Th>In person</Th><Th>Virtual</Th>
          <Th>Companies</Th><Th>New / Repeat</Th><Th>/ week</Th>
          <Th>% with outcome</Th><Th>Sales opp. (USD)</Th><Th>Purchase opp. (USD)</Th><Th>Business 60d (USD)</Th><Th>On-time reports</Th>
          <Th>Missing</Th><Th>Late</Th><Th>Sent back</Th><Th>Open actions</Th><Th>Overdue actions</Th>
        </tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={`${r.kind}-${r.id}`} className="border-b last:border-0" data-testid={`visits-by-person-row-${r.id}`}>
            <Td>{r.name}{r.kind === 'visitor' ? ' (field visitor)' : ''}{r.kind === 'untagged' ? ' (typed, to tag)' : ''}</Td>
            <Td>{r.meetings_in_person}</Td><Td>{r.meetings_virtual}</Td>
            <Td>{r.unique_companies}</Td><Td>{r.new_companies} / {r.repeat_companies}</Td>
            <Td>{r.meetings_per_week}</Td><Td>{pct(r.pct_with_outcome)}</Td>
            <Td>{usd(r.sales_opportunities_usd)}</Td><Td>{usd(r.purchase_opportunities_usd)}</Td><Td>{usd(r.business_done_60d_usd)}</Td>
            <Td>{r.reports_on_time_pct != null ? `${r.reports_on_time_pct}%` : '—'}</Td>
            <Td>{r.missing_reports}</Td><Td>{r.late_reports}</Td><Td>{r.sent_back_reports}</Td>
            <Td>{r.open_actions}</Td><Td>{r.overdue_actions}</Td>
          </tr>
        ))}</tbody>
      </table>
    );
  }
  return null;
}
